import { isAfter } from "date-fns";
import * as yup from "yup";
import { Intervention } from "./types";

function isTimestampInvalid(_: string, originalValue: number) {
  if (isAfter(originalValue, new Date(1970, 1, 1))) {
    return false;
  } else if (originalValue === null) {
    return false;
  }
  return true;
}

const urlRegex =
  /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)?/gi;
const websiteUrlRegex = /^(projects|archived-projects)\/[a-zA-Z-]+$/;

export const interventionItem = yup
  .object({
    action: yup.array().of(yup.string()).nullable().default([]),
    ecosystem: yup.array().of(yup.string()).nullable().default([]),
    interventionCounter: yup.number(),
  })
  .test({
    name: "paired-selection",
    test: (value: Intervention | undefined, context) => {
      const hasAction =
        Array.isArray(value?.action) && value!.action!.length > 0;
      const hasEcosystem =
        Array.isArray(value?.ecosystem) && value!.ecosystem!.length > 0;

      if (hasAction && !hasEcosystem) {
        return context.createError({
          path: `${context.path}.ecosystem`,
          message:
            "Must select corresponding 'ecosystem' where 'action' is selected",
        });
      }
      if (hasEcosystem && !hasAction) {
        return context.createError({
          path: `${context.path}.action`,
          message:
            "Must select corresponding 'action' where 'ecosystem' is selected",
        });
      }
      return true;
    },
  });
const interventionsSchema = yup.array().of(interventionItem);

const hasOutcomeValues = (outcome: {
  unit?: string;
  value?: string;
  "unit-of-sale"?: boolean;
  "outcome-start-date"?: number | null;
  "outcome-end-date"?: number | null;
  outcomeCounter?: number;
  origin?: "saved" | "draft";
}) =>
  Boolean(
    outcome.unit ||
    outcome.value ||
    outcome["unit-of-sale"] ||
    outcome["outcome-start-date"] ||
    outcome["outcome-end-date"],
  );

type OutcomeForValidation = {
  unit?: string;
  value?: string;
  "unit-of-sale"?: boolean;
  "outcome-start-date"?: number | null;
  "outcome-end-date"?: number | null;
  outcomeCounter?: number;
  origin?: "saved" | "draft";
};

type IndexedOutcomeForValidation = {
  outcome: OutcomeForValidation;
  index: number;
};

const hasTimestamp = (
  timestamp: number | null | undefined,
): timestamp is number => timestamp !== null && timestamp !== undefined;

const isValidTimestamp = (
  timestamp: number | null | undefined,
): timestamp is number => hasTimestamp(timestamp) && Number.isFinite(timestamp);

const groupOutcomesByUnit = (
  outcomes: Array<IndexedOutcomeForValidation>,
): Record<string, Array<IndexedOutcomeForValidation>> =>
  outcomes.reduce<Record<string, Array<IndexedOutcomeForValidation>>>(
    (groupedOutcomes, indexedOutcome) => {
      const unit = indexedOutcome.outcome.unit!;
      groupedOutcomes[unit] = [
        ...(groupedOutcomes[unit] || []),
        indexedOutcome,
      ];
      return groupedOutcomes;
    },
    {},
  );

const sortOutcomesByStartDate = (
  firstOutcome: IndexedOutcomeForValidation,
  secondOutcome: IndexedOutcomeForValidation,
) =>
  (firstOutcome.outcome["outcome-start-date"] ?? Number.MAX_SAFE_INTEGER) ===
  (secondOutcome.outcome["outcome-start-date"] ?? Number.MAX_SAFE_INTEGER)
    ? (firstOutcome.outcome.outcomeCounter || 0) -
      (secondOutcome.outcome.outcomeCounter || 0)
    : (firstOutcome.outcome["outcome-start-date"] ?? Number.MAX_SAFE_INTEGER) -
      (secondOutcome.outcome["outcome-start-date"] ?? Number.MAX_SAFE_INTEGER);

const doOutcomePeriodsOverlap = (
  firstOutcome: OutcomeForValidation,
  secondOutcome: OutcomeForValidation,
) => {
  const firstStartDate = firstOutcome["outcome-start-date"];
  const secondStartDate = secondOutcome["outcome-start-date"];

  if (!isValidTimestamp(firstStartDate) || !isValidTimestamp(secondStartDate)) {
    return false;
  }

  const firstEndDate =
    firstOutcome["outcome-end-date"] ?? Number.MAX_SAFE_INTEGER;
  const secondEndDate =
    secondOutcome["outcome-end-date"] ?? Number.MAX_SAFE_INTEGER;

  return firstStartDate <= secondEndDate && secondStartDate <= firstEndDate;
};

const outcomeItem = yup.object().shape({
  outcomeCounter: yup.number(),
  origin: yup.mixed<"saved" | "draft">().oneOf(["saved", "draft"]).optional(),
  unit: yup
    .string()
    .test({
      name: "saved-outcome-not-empty",
      test: (_, context) => {
        if (
          context.parent.origin === "saved" &&
          !hasOutcomeValues(context.parent)
        ) {
          return context.createError({
            message: {
              message: "Saved outcomes cannot be cleared",
              outcomeCounter: context.parent.outcomeCounter,
            },
          });
        }
        return true;
      },
    })
    .test({
      name: "outcome-unit",
      test: (_, context) => {
        const { value, unit } = context.parent;

        if (value && !unit) {
          return context.createError({
            message: {
              message: "Must select Unit where Value is selected",
              outcomeCounter: context.parent.outcomeCounter,
            },
          });
        }
        return true;
      },
    }),
  value: yup
    .string()
    .test({
      name: "outcome-value",
      test: (value, context) => {
        if (value && parseFloat(value) <= 0) {
          return context.createError({
            message: {
              message: "The value can't be 0 or negative",
              outcomeCounter: context.parent.outcomeCounter,
            },
          });
        }
        return true;
      },
    })
    .test({
      name: "outcome-value",
      test: (value, context) => {
        if (value && isNaN(parseFloat(value))) {
          return context.createError({
            message: {
              message: "Must be a number",
              outcomeCounter: context.parent.outcomeCounter,
            },
          });
        }
        return true;
      },
    })
    .test({
      name: "outcome-value",
      test: (_, context) => {
        const { value, unit } = context.parent;
        if (unit && !value) {
          return context.createError({
            message: {
              message: "Must select Value where Unit is selected",
              outcomeCounter: context.parent.outcomeCounter,
            },
          });
        }
        return true;
      },
    }),
  "outcome-start-date": yup
    .mixed<number>()
    .nullable()
    .test({
      name: "outcome-start-date-required",
      test: (startDateValue, context) => {
        if (hasOutcomeValues(context.parent) && !hasTimestamp(startDateValue)) {
          return context.createError({
            message: {
              message: "Start date is required",
              outcomeCounter: context.parent.outcomeCounter,
            },
          });
        }
        return true;
      },
    })
    .test({
      name: "outcome-start-date-valid",
      test: (startDateValue, context) => {
        if (hasTimestamp(startDateValue) && !isValidTimestamp(startDateValue)) {
          return context.createError({
            message: {
              message: "Must be a valid date in dd/mm/yyyy",
              outcomeCounter: context.parent.outcomeCounter,
            },
          });
        }
        return true;
      },
    }),
  "outcome-end-date": yup
    .mixed<number>()
    .nullable()
    .test({
      name: "outcome-end-date-before-start",
      test: (endDateValue, context) => {
        const startDate = context.parent["outcome-start-date"];

        if (
          isValidTimestamp(startDate) &&
          isValidTimestamp(endDateValue) &&
          endDateValue <= startDate
        ) {
          return context.createError({
            message: {
              message: "End date must be after start date",
              outcomeCounter: context.parent.outcomeCounter,
            },
          });
        }
        return true;
      },
    })
    .test({
      name: "outcome-end-date-valid",
      test: (endDateValue, context) => {
        if (
          hasTimestamp(endDateValue) &&
          (!isValidTimestamp(endDateValue) ||
            !isAfter(endDateValue as number, new Date(1970, 1, 1)))
        ) {
          return context.createError({
            message: {
              message: "Must be a valid date in dd/mm/yyyy",
              outcomeCounter: context.parent.outcomeCounter,
            },
          });
        }
        return true;
      },
    }),
});

const outcomesSchema = yup
  .array()
  .of(outcomeItem)
  .test({
    name: "historical-outcomes-require-end-date",
    test: (outcomes, context) => {
      if (!outcomes) {
        return true;
      }

      const populatedOutcomes = (outcomes as Array<OutcomeForValidation>)
        .map((outcome, index) => ({ outcome, index }))
        .filter(
          ({ outcome }) =>
            hasOutcomeValues(outcome) &&
            outcome.unit &&
            isValidTimestamp(outcome["outcome-start-date"]),
        );

      const outcomesByUnit = groupOutcomesByUnit(populatedOutcomes);

      for (const sameUnitOutcomes of Object.values(outcomesByUnit)) {
        if (sameUnitOutcomes.length <= 1) {
          continue;
        }

        const sortedOutcomes = [...sameUnitOutcomes].sort(
          sortOutcomesByStartDate,
        );

        for (let i = 0; i < sortedOutcomes.length - 1; i++) {
          const historicalOutcome = sortedOutcomes[i];
          if (!historicalOutcome.outcome["outcome-end-date"]) {
            return context.createError({
              path: `${context.path}[${historicalOutcome.index}].outcome-end-date`,
              message: {
                message: "End date is required on historical outcomes",
                outcomeCounter: historicalOutcome.outcome.outcomeCounter,
              },
            });
          }
        }
      }

      return true;
    },
  })
  .test({
    name: "same-unit-periods-do-not-overlap",
    test: (outcomes, context) => {
      if (!outcomes) {
        return true;
      }

      const populatedOutcomes = (outcomes as Array<OutcomeForValidation>)
        .map((outcome, index) => ({ outcome, index }))
        .filter(
          ({ outcome }) =>
            hasOutcomeValues(outcome) &&
            outcome.unit &&
            isValidTimestamp(outcome["outcome-start-date"]),
        );

      const outcomesByUnit = groupOutcomesByUnit(populatedOutcomes);

      for (const sameUnitOutcomes of Object.values(outcomesByUnit)) {
        const sortedOutcomes = [...sameUnitOutcomes].sort(
          sortOutcomesByStartDate,
        );

        for (let i = 0; i < sortedOutcomes.length; i++) {
          const currentOutcome = sortedOutcomes[i];

          for (let j = 0; j < i; j++) {
            const previousOutcome = sortedOutcomes[j];

            if (
              doOutcomePeriodsOverlap(
                previousOutcome.outcome,
                currentOutcome.outcome,
              )
            ) {
              return context.createError({
                path: `${context.path}[${currentOutcome.index}].outcome-start-date`,
                message: {
                  message: "Outcomes with the same unit cannot overlap",
                  outcomeCounter: currentOutcome.outcome.outcomeCounter,
                },
              });
            }
          }
        }
      }

      return true;
    },
  })
  .test({
    name: "unit-of-sale-periods-do-not-overlap",
    test: (outcomes, context) => {
      if (!outcomes) {
        return true;
      }

      const selectedUnitOfSaleOutcomes = (
        outcomes as Array<OutcomeForValidation>
      )
        .map((outcome, index) => ({ outcome, index }))
        .filter(
          ({ outcome }) =>
            hasOutcomeValues(outcome) &&
            outcome["unit-of-sale"] &&
            isValidTimestamp(outcome["outcome-start-date"]),
        )
        .sort(sortOutcomesByStartDate);

      for (let i = 0; i < selectedUnitOfSaleOutcomes.length; i++) {
        const currentOutcome = selectedUnitOfSaleOutcomes[i];

        for (let j = 0; j < i; j++) {
          const previousOutcome = selectedUnitOfSaleOutcomes[j];

          if (
            doOutcomePeriodsOverlap(
              previousOutcome.outcome,
              currentOutcome.outcome,
            )
          ) {
            return context.createError({
              path: `${context.path}[${currentOutcome.index}].outcome-start-date`,
              message: {
                message: "Unit of sale periods cannot overlap",
                outcomeCounter: currentOutcome.outcome.outcomeCounter,
              },
            });
          }
        }
      }

      return true;
    },
  })
  .test({
    name: "unit-of-sale-outcomes-use-same-unit",
    test: (outcomes, context) => {
      if (!outcomes) {
        return true;
      }

      const selectedUnitOfSaleOutcomes = (
        outcomes as Array<OutcomeForValidation>
      )
        .map((outcome, index) => ({ outcome, index }))
        .filter(
          ({ outcome }) =>
            hasOutcomeValues(outcome) &&
            outcome["unit-of-sale"] &&
            outcome.unit,
        )
        .sort(sortOutcomesByStartDate);

      if (selectedUnitOfSaleOutcomes.length <= 1) {
        return true;
      }

      const firstUnitOfSaleOutcome = selectedUnitOfSaleOutcomes[0];
      const firstUnitOfSaleUnit = firstUnitOfSaleOutcome.outcome.unit;

      for (let i = 1; i < selectedUnitOfSaleOutcomes.length; i++) {
        const currentOutcome = selectedUnitOfSaleOutcomes[i];

        if (currentOutcome.outcome.unit !== firstUnitOfSaleUnit) {
          return context.createError({
            path: `${context.path}[${currentOutcome.index}].unit`,
            message: {
              message: "All unit of sale outcomes must use the same unit",
              outcomeCounter: currentOutcome.outcome.outcomeCounter,
            },
          });
        }
      }

      return true;
    },
  });

export const solutionForm = yup.object().shape(
  {
    "solution-name": yup.string().required("Project name is required"),
    "solution-vbc-website-display-name": yup.string().when("outcomes", {
      is: (outcomes: Array<{ unit: string; "unit-of-sale": boolean }>) =>
        outcomes?.some(
          (outcome) =>
            outcome.unit === "Biodiversity" && outcome["unit-of-sale"],
        ),
      then: (schema) =>
        schema.required(
          "VBC Website Display Name is required when Biodiversity is the unit of sale",
        ),
    }),
    "solution-assessment-methodology": yup
      .string()
      .required("Assessment methodology is required"),
    "solution-start-date": yup
      .boolean()
      .transform(isTimestampInvalid)
      .isFalse("Must be a valid date in dd/mm/yyyy"),
    "solution-end-date": yup
      .boolean()
      .transform(isTimestampInvalid)
      .isFalse("Must be a valid date in dd/mm/yyyy"),
    latitude: yup
      .number()
      .test(
        "longitude-dependency",
        "Both latitude and longitude must be provided",
        (value, context) => {
          const { longitude } = context.parent;
          return !(value && !longitude);
        },
      )
      .min(-90, "Latitude must be between -90 and 90 degrees")
      .max(90, "Latitude must be between -90 and 90 degrees")
      .typeError("Must be a number"),
    longitude: yup
      .number()
      .test(
        "latitude-dependency",
        "Both longitude and latitude must be provided",
        (value, context) => {
          const { latitude } = context.parent;
          return !(value && !latitude);
        },
      )
      .min(-180, "Longitude must be between -180 and 180 degrees")
      .max(180, "Longitude must be between -180 and 180 degrees")
      .typeError("Must be a number"),
    "solution-intervention-type": yup
      .string()
      .required("Intervention type is a required field"),
    interventions: interventionsSchema,
    outcomes: outcomesSchema,
    "programme-info": yup.object().shape({
      "asset-type": yup
        .string()
        .required("Asset (credit) type is a required field"),
      "description-document": yup
        .string()
        .matches(urlRegex, "Must be a valid URL"),
      "carbon-crediting-programme": yup
        .array()
        .of(yup.string())
        .min(1, "Carbon crediting programme is a required field")
        .required("Carbon crediting programme is a required field"),
    }),
    "internal-details": yup.object().shape({
      "website-url": yup
        .string()
        .matches(
          websiteUrlRegex,
          "Please paste the Marketing website url starting from 'projects', example: projects/mangroves-maroalika or archived-projects/mangroves-maroalika",
        ),
      "xero-account-number": yup
        .string()
        .max(10, "Can't be longer than 10 characters"),
      "additional-resources-link": yup
        .string()
        .matches(urlRegex, "Must be a valid URL"),
    }),
    "risk-monitoring": yup.object().nullable().shape({
      "is-risk-alert-enabled": yup.boolean(),
      "buffer-value-km": yup.number(),
      "geospatial-file": yup.mixed().nullable(),
      "geospatial-id": yup.string().nullable(),
    }),
  },
  [
    ["outcome.unit", "outcome.value"],
    ["outcome.value", "outcome.unit"],
    ["latitude", "longitude"],
    ["longitude", "latitude"],
  ],
);
